/**
 * SelectionToolSidebar Component
 *
 * Pure content panel for bulk booking operations.
 * Shows when edit mode is enabled.
 *
 * Features:
 * - Quick marking (all days / no days)
 * - Day-of-week checkboxes (Mon-Fri)
 * - Selection counter
 * - Action buttons (Add, Edit, Delete)
 *
 * Note: This is a pure content component. Layout concerns like
 * overlay, collapse buttons, and positioning are handled by the
 * parent (e.g., PanelSplitView).
 *
 * Ported from Vue SelectionTool.vue
 */

import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { DividerWithText } from "@/components/ui/display/DividerWithText";
import { HintText } from "@/components/ui/display/HintText";
import { Icon } from "@/components/ui/display/Icon";
import { SectionHeader } from "@/components/ui/navigation/SectionHeader";
import { useBookingSelection } from "@/hooks/booking/useBookingSelection";
import classNames from "classnames";
import React, { useCallback } from "react";
import styles from "./SelectionToolSidebar.module.css";
import type { SelectionToolSidebarProps } from "./types";

const DAY_NAMES = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag"];
const WEEKEND_NAMES = ["Lørdag", "Søndag"];

export const SelectionToolSidebar: React.FC<SelectionToolSidebarProps> = ({
  onAction,
  showWeekends = false,
  className,
  "data-testid": testId,
}) => {
  const {
    selectionCount,
    hasSelection,
    isAllSelected,
    hasSelectedCardsWithBookings,
    selectAll,
    clearSelection,
    isDayOfWeekSelected,
    isDayOfWeekPartiallySelected,
    toggleDayOfWeek,
  } = useBookingSelection();

  // Formatted selection count string
  const selectionCountString =
    selectionCount === 1 ? "1 dag" : `${selectionCount} dager`;

  // Handle action button clicks
  const handleAdd = useCallback(() => {
    onAction?.("add");
  }, [onAction]);

  const handleEdit = useCallback(() => {
    onAction?.("edit");
  }, [onAction]);

  const handleDelete = useCallback(() => {
    onAction?.("delete");
  }, [onAction]);

  // Days to show (weekdays only by default, or all 7 if showWeekends)
  const dayLabels = showWeekends ? [...DAY_NAMES, ...WEEKEND_NAMES] : DAY_NAMES;

  return (
    <div className={classNames(styles.sidebar, className)} data-testid={testId}>
      {/* Header */}
      <h2 className={styles.title}>Rediger bookinger</h2>

      {/* Instructions */}
      <div className={styles.hints}>
        <HintText icon={<Icon name="view-grid-plus" size="sm" />}>
          Klikk på dager for å markere.
        </HintText>
        <HintText icon={<Icon name="arrow-big-up" size="sm" />}>
          Hold inne shift for å velge periode; velg først startdato, så
          sluttdato
        </HintText>
      </div>

      {/* Quick Marking Section */}
      <div className={styles.section}>
        <SectionHeader as="h3" size="sm">
          Hurtigmarkering
        </SectionHeader>
        <div className={styles.toolBox}>
          {/* All / None toggle buttons */}
          <div className={styles.toggleRow}>
            <ActionButton
              variant="info"
              rounded={true}
              label="Alle dager"
              size="small"
              selected={isAllSelected}
              disabled={isAllSelected}
              onClick={selectAll}
              data-testid="bulk-select-all-btn"
            />
            <ActionButton
              variant="info"
              rounded={true}
              label="Ingen dager"
              size="small"
              selected={!hasSelection}
              disabled={!hasSelection}
              onClick={clearSelection}
              data-testid="bulk-deselect-all-btn"
            />
          </div>

          {/* Day-of-week radio buttons */}
          <div className={styles.dayGrid}>
            {dayLabels.map((dayName, index) => {
              const isChecked = isDayOfWeekSelected(index);
              const isIndeterminate = isDayOfWeekPartiallySelected(index);

              return (
                <label
                  key={dayName}
                  className={styles.dayOption}
                  data-testid={`day-checkbox-${index}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleDayOfWeek(index)}
                  />
                  <span
                    className={classNames(
                      styles.dayRadio,
                      isChecked && styles.checked,
                      isIndeterminate && !isChecked && styles.indeterminate,
                    )}
                  />
                  <span className={styles.dayLabel}>{dayName}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selection Summary with decorative lines */}
      <DividerWithText
        className={styles.summary}
        legend={`${selectionCountString} markert`}
        data-testid="bulk-selection-count"
      >
        {/* Action Buttons */}
        <ActionButton
          rounded={true}
          label="Legg til"
          icon={<Icon name="plus" size="sm" />}
          variant="primary"
          disabled={!hasSelection}
          onClick={handleAdd}
          data-testid="bulk-add-btn"
        />
        <ActionButton
          rounded={true}
          label="Rediger"
          icon={<Icon name="pencil" size="sm" />}
          variant="highlight"
          disabled={!hasSelectedCardsWithBookings}
          onClick={handleEdit}
          data-testid="bulk-edit-btn"
        />
        <ActionButton
          rounded={true}
          label="Slett"
          icon={<Icon name="trash" size="sm" />}
          variant="danger"
          disabled={!hasSelectedCardsWithBookings}
          onClick={handleDelete}
          data-testid="bulk-delete-btn"
        />
      </DividerWithText>
    </div>
  );
};

SelectionToolSidebar.displayName = "SelectionToolSidebar";

export default SelectionToolSidebar;
